import { useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { weeklyReportApi, type InternalWeeklyReport, type WeeklyReportFiledBlock, type WeeklyReportFiledView } from '@/lib/apiClient';
import { Button } from '@/components';
import { downloadFromJobFolder, GraphConsentError } from '@/lib/graphUpload';
import { readFinalizedReport, type ReadReport } from './weeklyReportRead';
import { fileWarnings, MAX_UPLOAD_BYTES, REPORTS_SUBFOLDER, uploadedOn } from './FinalizedReports';
import ReportPhotoGrid from './ReportPhotoGrid';

/**
 * The week's finalized internal report AS FILED (#576 follow-up, product
 * owner 2026-10-12): the "After upload" side of the tab's toggle, so whoever
 * reads the week's report can read all of it here, without opening the Word
 * file in SharePoint.
 *
 * Every section, line and table read from the uploaded file, in the file's
 * order, set like the draft beside it. Where the file had a day's photos, the
 * Tracker's own site photos for that day show; images pasted into Word by
 * hand aren't kept. Styled by WeeklyReportTab's `wr-` classes, mounted with it.
 *
 * A report uploaded before whole files were read has no text here: an editor
 * can read it in from the file in SharePoint (`rereadUpload`), which also
 * refreshes what it carries into the next week.
 */
export default function FiledReport({
  projectId, report, filed, thumbs, settled, needsSignIn, onSignIn, canEdit, locked, jobFolderUrl, getToken,
}: {
  projectId: number;
  report: InternalWeeklyReport;
  filed: WeeklyReportFiledView;
  thumbs: Record<string, string>;
  settled: (u: string) => boolean;
  /** SharePoint needs a sign-in before the photos can load; `onSignIn` opens it. */
  needsSignIn: boolean;
  onSignIn: () => void;
  canEdit: boolean;
  /** No Sage job number yet: nothing can be filed, or read in, on the job. */
  locked: boolean;
  /**
   * The job's Job Folder, the fallback for finding the file by name; null when
   * none is linked, undefined while the job is still loading (then nothing is
   * offered or explained yet).
   */
  jobFolderUrl: string | null | undefined;
  /** The SharePoint token, taken inside the click (it may open the sign-in popup). */
  getToken: () => Promise<string>;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A read that looks like the wrong copy or week, held until the editor confirms it (or cancels). */
  const [held, setHeld] = useState<{ read: ReadReport; warnings: string[] } | null>(null);
  const busyRef = useRef(false);
  const content = filed.content;
  // The file's own link finds it; the Job Folder is only the fallback, so it isn't required.
  const known = jobFolderUrl !== undefined;
  const canReread = canEdit && !locked && known;

  /**
   * Read the file again and store what it says. ⚠️ The file may have been
   * replaced in SharePoint since it was uploaded, so the upload window's own
   * checks run first (fileWarnings): a file that reads as the client copy, or
   * another week's report, is held for the editor to confirm rather than
   * stored over this week's report and carried into next week's.
   */
  const reread = async (confirmed?: ReadReport) => {
    if (!canReread || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setHeld(null);
    try {
      let read = confirmed;
      if (!read) {
        // The token first, inside the click: it may open the sign-in popup.
        const token = await getToken();
        const bytes = await downloadFromJobFolder({
          getToken: async () => token, jobFolderUrl: jobFolderUrl ?? null, subfolder: REPORTS_SUBFOLDER,
          fileName: filed.fileName ?? 'the report', webUrl: filed.webUrl,
          // The same limit an upload has: past it, it isn't the report.
          maxBytes: MAX_UPLOAD_BYTES,
        });
        read = await readFinalizedReport(bytes, report.window.weekStart, { report });
        const warnings = fileWarnings(read, 'internal', report.window.weekStart);
        if (warnings.length) { setHeld({ read, warnings }); return; }
      }
      await weeklyReportApi.rereadUpload(projectId, filed.uploadId, { carryForward: read.carry, filed: read.filed });
      // This week's view, and every later week's draft, which starts from it.
      await qc.invalidateQueries({ queryKey: ['weekly-report', projectId] });
    } catch (e) {
      setError(e instanceof GraphConsentError
        ? 'SharePoint access isn’t available (sign-in declined, or admin consent pending). Try again once it is.'
        : `Couldn’t read the file: ${e instanceof Error ? e.message : 'unknown error'}`);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const uploaded = uploadedOn(filed.uploadedAt);
  const readAgain = filed.readAgain ? uploadedOn(filed.readAgain.at) : '';
  // The same offer the draft makes: this view is where a week with a filed
  // report opens, so without it a viewer not yet signed in sees only names.
  const showsPhotos = !!content?.sections.some((s) => s.blocks.some((b) => b.type === 'photos'
    && report.days.some((d) => d.date === b.date && d.photoPosts.some((p) => p.photos.length > 0))));
  return (
    <div className="wr-doc" data-testid="filed-report">
      <div className="wr-notice wr-notice-info">
        The finalized internal report as uploaded{uploaded ? ` ${uploaded}` : ''}{filed.uploadedBy ? ` by ${filed.uploadedBy}` : ''}
        {/* Said, so a later read of an edited file isn't credited to the uploader. */}
        {filed.readAgain && <>, read again from the file{readAgain ? ` ${readAgain}` : ''}{filed.readAgain.by ? ` by ${filed.readAgain.by}` : ''}</>}
        {' · '}
        <a href={filed.webUrl} target="_blank" rel="noopener noreferrer">{filed.fileName ?? 'Open in SharePoint'}</a>
        {content && canReread && (
          <>
            {' · '}
            <button type="button" className="ew-btn-link" disabled={busy} onClick={() => void reread()}>
              {busy ? 'Reading the file…' : 'Read the file again'}
            </button>
          </>
        )}
      </div>
      {needsSignIn && showsPhotos && (
        <div className="wr-notice wr-notice-info" style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-3)', flexWrap: 'wrap' }}>
          Sign in to SharePoint to see the photos here.
          <Button size="sm" variant="secondary" onClick={onSignIn}>Show photos</Button>
        </div>
      )}

      {!content ? (
        <div className="wr-word" style={{ fontStyle: 'normal' }}>
          <p style={{ margin: '0 0 8px' }}>
            This report&rsquo;s text hasn&rsquo;t been read into the Project Tracker (it was uploaded before whole reports were read),
            so only the Word file has it.
          </p>
          {canReread
            ? <Button size="sm" onClick={() => void reread()} isLoading={busy}>Read the full report from the file</Button>
            : !canEdit
              ? <span>Someone who can edit the job can read it in from the file in SharePoint.</span>
              : locked
                ? <span>It can be read in once the job has a Sage job number.</span>
                : null /* the job is still loading */}
        </div>
      ) : content.sections.length === 0 ? (
        <div className="wr-word">Nothing could be read from this file. Open it in SharePoint to see it.</div>
      ) : (
        content.sections.map((s, i) => (
          <section className="wr-sec" key={i}>
            {s.title && <h2 className="wr-sec-hd">{s.title}</h2>}
            <div className="wr-sec-body wr-filed">
              {s.blocks.length ? <Blocks blocks={s.blocks} photos={{ report, thumbs, settled }} /> : <p className="wr-muted">Left empty.</p>}
            </div>
          </section>
        ))
      )}

      {held && (
        <div className="wr-notice wr-notice-err" role="alert">
          {held.warnings.map((w) => <div key={w}>{w}</div>)}
          <div style={{ marginTop: 8, display: 'flex', gap: 'var(--ew-s-2)', flexWrap: 'wrap' }}>
            <Button size="sm" onClick={() => void reread(held.read)}>It&rsquo;s the right file. Read it in</Button>
            <Button size="sm" variant="secondary" onClick={() => setHeld(null)}>Cancel</Button>
          </div>
        </div>
      )}
      {content?.truncated && (
        <div className="wr-notice wr-notice-info">The file is longer than the Project Tracker keeps; the rest is in the Word file.</div>
      )}
      {error && <div className="wr-notice wr-notice-err" role="alert">{error}</div>}
    </div>
  );
}

/**
 * A section's blocks in order, consecutive bullets as one list: the filed
 * report's, and a section the PM added as the draft carries it. Photos show
 * only with `photos` (the filed report); a carried section has none.
 */
export function Blocks({ blocks, photos }: {
  blocks: readonly WeeklyReportFiledBlock[];
  photos?: { report: InternalWeeklyReport; thumbs: Record<string, string>; settled: (u: string) => boolean };
}) {
  const out: ReactNode[] = [];
  let bullets: string[] = [];
  const flush = (key: number) => {
    if (bullets.length) out.push(<ul className="wr-list" key={`ul-${key}`}>{bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>);
    bullets = [];
  };
  blocks.forEach((b, i) => {
    if (b.type === 'text' && b.style === 'bullet') { bullets.push(b.text); return; }
    flush(i);
    if (b.type === 'text') {
      if (b.style === 'heading') {
        out.push(b.date
          ? <div className="wr-day-hd" key={i}><span>{b.text}</span></div>
          : <h3 className="wr-sub" key={i}>{b.text}</h3>);
      } else {
        out.push(<p key={i}>{b.text}</p>);
      }
    } else if (b.type === 'table') {
      out.push(<ReportTable key={i} rows={b.rows} header={b.header} />);
    } else if (photos) {
      // The file had this day's photos here: the Tracker's own, for the same day.
      const day = b.date ? photos.report.days.find((d) => d.date === b.date) : undefined;
      out.push(day && day.photoPosts.some((p) => p.photos.length > 0)
        ? <ReportPhotoGrid key={i} day={day} thumbs={photos.thumbs} settled={photos.settled} />
        : <p key={i} className="wr-muted">Photos are in the Word file.</p>);
    }
  });
  flush(blocks.length);
  return <>{out}</>;
}

/** A table as typed in Word: its first row the header when it was one. */
export function ReportTable({ rows, header }: { rows: string[][]; header: boolean }) {
  const cols = Math.max(1, ...rows.map((r) => r.length));
  const cells = (r: string[]) => Array.from({ length: cols }, (_, i) => r[i] ?? '');
  const [head, ...rest] = rows;
  return (
    <div className="ew-table-scroll" style={{ margin: '4px 0 8px' }}>
      <table className="wr-table">
        {header && head && <thead><tr>{cells(head).map((c, i) => <th key={i}>{c}</th>)}</tr></thead>}
        <tbody>
          {(header ? rest : rows).map((r, ri) => <tr key={ri}>{cells(r).map((c, i) => <td key={i}>{c || '—'}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}
